import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export type MobilePlatform = 'ios' | 'android' | 'other';

/**
 * Lightweight user-agent based device detection, plus the logic for handing
 * off from a public /links/* web page to the native app. Used by the
 * "Open in the App" banner and by the primary CTAs on pages (like the event
 * invite page) that have no meaningful web-only action of their own.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  private readonly appStoreUrl = `https://apps.apple.com/app/id${environment.APPSTORE_ID}`;
  private readonly playStoreUrl = `https://play.google.com/store/apps/details?id=${environment.ANDROID_PACKAGE_NAME}`;

  getMobilePlatform(): MobilePlatform {
    if (typeof navigator === 'undefined') return 'other';
    const ua = navigator.userAgent || '';

    if (/android/i.test(ua)) return 'android';

    // iOS - covers iPhone/iPad/iPod, plus iPadOS 13+ which reports as Mac
    // but exposes touch support (desktop Macs do not).
    const isIosLike =
      /iphone|ipad|ipod/i.test(ua) ||
      (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    if (isIosLike) return 'ios';

    return 'other';
  }

  isMobile(): boolean {
    return this.getMobilePlatform() !== 'other';
  }

  /**
   * Try to hand off to the native app for the given path (relative to the
   * web domain, no leading slash, e.g. `links/groupinvite?groupId=abc`).
   * Falls through to the appropriate app store if the app isn't installed.
   * No-op on desktop.
   */
  openApp(path: string): void {
    const platform = this.getMobilePlatform();
    const cleanPath = path.replace(/^\/+/, '');

    if (platform === 'android') {
      // intent:// lets Chrome try the native app directly and, if it isn't
      // installed, fall back to the Play Store automatically - no timers
      // or visibility hacks needed.
      const intentUrl =
        `intent://web.atchurch.app/${cleanPath}#Intent;scheme=https;` +
        `package=${environment.ANDROID_PACKAGE_NAME};` +
        `S.browser_fallback_url=${encodeURIComponent(this.playStoreUrl)};end`;
      window.location.href = intentUrl;
      return;
    }

    if (platform === 'ios') {
      // iOS has no equivalent of Android's browser_fallback_url, so we try
      // the custom URL scheme and, if the app doesn't take over within a
      // short window (page stays visible), send them to the App Store.
      let fallbackTriggered = false;
      const toStore = () => {
        if (fallbackTriggered || document.visibilityState === 'hidden') {
          return;
        }
        fallbackTriggered = true;
        window.location.href = this.appStoreUrl;
      };

      document.addEventListener('visibilitychange', function onVis() {
        if (document.visibilityState === 'hidden') {
          fallbackTriggered = true;
          document.removeEventListener('visibilitychange', onVis);
        }
      });

      window.location.href = `${environment.APP_URL_SCHEME}://${cleanPath}`;
      setTimeout(toStore, 1500);
    }
  }

  currentPath(): string {
    if (typeof window === 'undefined') return '';
    return `${window.location.pathname}${window.location.search}`.replace(
      /^\/+/,
      '',
    );
  }
}
